// Mochi's weekly cover-pass helpers — pure functions, no React.
//
// The pass is shame-safe by design: we never call it a "cheat" or "skip"
// in any user-facing string. It is framed as an act of friendship.
//
// Rotating phrase banks for each part of the interaction (offer / accept /
// decline) live here so the UI can re-use them in multiple surfaces
// (speech bubble, notification, pencil menu).

// Today's EST date in YYYY-MM-DD, matching backend's `today_str()`.
// Reuses Intl rather than pulling a tz library — Mochi's clock is EST.
export function todayEastern() {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: "America/New_York",
        year: "numeric", month: "2-digit", day: "2-digit",
    });
    return fmt.format(new Date()); // "YYYY-MM-DD"
}

// Returns 0..6 with Monday=0 .. Sunday=6 (matches Habit.schedule_days).
export function easternWeekday() {
    const ymd = todayEastern().split("-").map(Number);
    // Construct a Date at noon EST on that day so DST never flips the weekday.
    const d = new Date(Date.UTC(ymd[0], ymd[1] - 1, ymd[2], 17, 0, 0));
    // d.getUTCDay(): Sunday=0..Saturday=6  →  remap to Monday=0..Sunday=6
    const jsDay = d.getUTCDay();
    return (jsDay + 6) % 7;
}

// Current hour 0..23 in EST.
export function easternHour() {
    const fmt = new Intl.DateTimeFormat("en-US", {
        timeZone: "America/New_York", hour: "numeric", hour12: false,
    });
    return parseInt(fmt.format(new Date()), 10);
}

// Habits at risk of losing their streak TODAY.
// At-risk = scheduled today + not yet completed + streak ≥ 3 + active habit.
export function findAtRiskHabits(habits, today = todayEastern(), weekday = easternWeekday()) {
    if (!Array.isArray(habits)) return [];
    return habits.filter((h) => {
        if (!h) return false;
        const scheduled = (h.schedule_days || [0, 1, 2, 3, 4, 5, 6]).includes(weekday);
        const done = (h.completed_dates || []).includes(today);
        const streak = h.streak || 0;
        return scheduled && !done && streak >= 3;
    });
}

// Phrase banks ---------------------------------------------------------------

const OFFER_PHRASES = [
    "hey… you've been on a roll with {habit}. i can cover for you today if you need it 🍡",
    "psst — {habit} streak is safe if you want. i've got a cover pass for you.",
    "you've worked hard on {habit}. want me to cover today? no shame either way 🌸",
    "i noticed {habit} didn't happen today. i can quietly cover it — just say the word.",
];

const ACCEPT_PHRASES = [
    "covered. your streak is safe. now rest 🌸",
    "got it. {habit} is covered for today 🍡 you rest.",
    "shhh — it's handled. streak intact.",
    "done. you've earned the night off ✨",
];

const DECLINE_PHRASES = [
    "okay 🍡 i'll keep it for when you really need it.",
    "no problem. it's here if you change your mind.",
    "saving it. you do you.",
];

// Notification phrase bank (used after 10pm EST for the streak-at-risk ping).
const NOTIFICATION_PHRASES = [
    "psst — your {habit} streak. i can cover tonight if you want 🍡",
    "still up? {habit} streak is safe — i've got you. just open the app.",
    "i have one cover pass left this week. {habit}? say the word.",
];

function _pick(pool, fill) {
    const line = pool[Math.floor(Math.random() * pool.length)];
    return fill ? line.replace("{habit}", fill) : line;
}

export const pickCoverOfferPhrase  = (habitName) => _pick(OFFER_PHRASES, habitName);
export const pickCoverAcceptPhrase = (habitName) => _pick(ACCEPT_PHRASES, habitName);
export const pickCoverDeclinePhrase = () => _pick(DECLINE_PHRASES);
export const pickCoverNotificationPhrase = (habitName) => _pick(NOTIFICATION_PHRASES, habitName);

// "Should Mochi offer the pass right now on the home screen?" (Situation 1)
// Returns the at-risk habit or null.
export function chooseHabitToOffer(habits, settings, { hour = easternHour() } = {}) {
    if (!settings || !settings.cover_pass_available) return null;
    if (hour < 20) return null; // after 8pm EST
    const atRisk = findAtRiskHabits(habits);
    if (atRisk.length === 0) return null;
    // Prefer the habit with the LONGEST streak (most worth saving).
    atRisk.sort((a, b) => (b.streak || 0) - (a.streak || 0));
    return atRisk[0];
}
